import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useClickAway from "@/core/helpers/useClickAway";
import { CrossIcon, NumOneIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isConfirming: boolean;
  price: number;
};

export default function EstatePurchaseModal({
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
  price,
}: Props) {
  const { t } = useTranslation("estate");

  const ref = useClickAway(onClose);

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={clsx(
            "w-[25.25rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center redShadow30",
          )}
        >
          <Button
            className="absolute top-2.5 right-2.5 shrink-0 size-7 p-0"
            color="gray"
            variant="soft"
            onClick={onClose}
          >
            <CrossIcon className="size-3" />
          </Button>
          <div className="text-18 font-bold text-white">{t("estate.unlockSlot")}</div>
          <div className="h-[1px] w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <div className="text-16 text-center font-medium">{t("estate.confirmPurchaseSlot")}</div>
          <div
            className={clsx(
              "w-full h-[2.75rem] flex items-center gap-2 justify-center mt-3.5",
              "rounded-[0.5rem] border border-dashed border-accent-solid",
              "bg-gradient-to-b from-[#E53F3F]/10 to-[#AB2929]/10",
            )}
          >
            <NumOneIcon className="size-5.5 text-accent-solid" />
            <div className="text-16 text-white font-bold">{formatPrice(price)}</div>
          </div>
          <div className="grid grid-cols-2 gap-2.5 w-full mt-2.5">
            <Button
              className="h-[2.625rem] basis-[50%]"
              onClick={onConfirm}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("estate.yes")}
            </Button>
            <Button
              className="h-[2.625rem] basis-[50%]"
              variant="solid-surface"
              onClick={onClose}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("estate.cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
